import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ShiftsService } from 'src/app/_services/shifts.service';
import { UsersService } from 'src/app/_services/admin/users.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { NgxSpinnerService } from 'ngx-spinner';
import moment from 'moment-timezone';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';

@Component({
  selector: 'app-assign-shifts',
  standalone: false,
  templateUrl: './assign-shifts.component.html',
  styleUrl: './assign-shifts.component.scss'
})
export class AssignShiftsComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  /** Set by ngx-bootstrap initialState when editing an existing assignment */
  data: any;
  mode: 'add' | 'edit' = 'add';
  /** 'users' | 'groups' — from parent Shifts list viewMode */
  viewMode: 'users' | 'groups' = 'users';
  activeTabIndex = 0;
  submitTouched = false;
  saving = false;
  assignmentId: string | number | null = null;

  shiftTypeOptions: any[] = [];
  groupOptions: any[] = [];
  userOptions: any[] = [];

  selectedUser: any = null;
  selectedUserShiftType: any = null;
  userStartDate: Date | null = null;
  userEndDate: Date | null = null;

  selectedGroup: any = null;
  selectedGroupShiftType: any = null;
  groupStartDate: Date | null = null;
  groupEndDate: Date | null = null;

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isUsersMode(): boolean {
    return this.viewMode === 'users';
  }

  get isGroupsMode(): boolean {
    return this.viewMode === 'groups';
  }

  /** When opened from user profile Shifts tab — user is fixed */
  get isUserLocked(): boolean {
    return !!this.data?.lockUser;
  }

  /** When opened from group view Shifts tab — group is fixed */
  get isGroupLocked(): boolean {
    return !!this.data?.lockGroup;
  }

  /** When opened from shift type details — shift type is fixed */
  get isShiftTypeLocked(): boolean {
    return !!this.data?.lockShiftType;
  }

  constructor(
    private languageTranslateService: LanguageTranslateService,
    private location: Location,
    public modalRef: BsModalRef,
    private shiftsService: ShiftsService,
    private userService: UsersService,
    private errorUtil: ErrorUtil,
    private spinner: NgxSpinnerService,
    private util: UtilServiceService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.resolveEditState();
    this.resolveViewMode();
    this.getLanguageData();
    this.loadLookupsThenAssignment();
  }

  private resolveEditState() {
    const id = this.data?.assignmentId ?? this.data?.id ?? null;
    const hasValidId =
      id != null && id !== '' && !String(id).startsWith('row-');
    if (hasValidId) {
      this.mode = 'edit';
      this.assignmentId = id;
    } else {
      this.mode = 'add';
      this.assignmentId = null;
    }
  }

  /** Prefer explicit viewMode from parent; fall back to assigneeType when editing */
  private resolveViewMode() {
    const fromParent = this.viewMode || this.data?.viewMode;
    if (fromParent === 'users' || fromParent === 'groups') {
      this.viewMode = fromParent;
      return;
    }
    const assigneeType = String(this.data?.assigneeType || '').toUpperCase();
    if (assigneeType === 'USER_GROUP' || assigneeType === 'GROUP') {
      this.viewMode = 'groups';
    } else {
      this.viewMode = 'users';
    }
  }

  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
        }
      })
    );
  }

  /********************************** Load dropdowns, then assignment details **************************/
  private loadLookupsThenAssignment() {
    this.spinner.show();

    const shiftTypes$ = this.shiftsService.getActiveShiftTypes().pipe(
      catchError((err: any) => {
        this.errorUtil.setErrorMessage(
          err.status,
          err.error ? err.error.title : null,
          err.error ? err.error.title : null,
          'error',
          2000
        );
        return of(null);
      })
    );

    const assignees$ = this.isGroupsMode
      ? this.userService.getAlluserGroup().pipe(
          catchError((err: any) => {
            this.errorUtil.setErrorMessage(
              err.status,
              err.error ? err.error.title : null,
              err.error ? err.error.title : null,
              'error',
              2000
            );
            return of([]);
          })
        )
      : this.userService.getActiveUsers().pipe(
          catchError((err: any) => {
            this.errorUtil.setErrorMessage(
              err.status,
              err.error ? err.error.title : null,
              err.error ? err.error.title : null,
              'error',
              2000
            );
            return of([]);
          })
        );

    this.push(
      forkJoin({ shiftTypes: shiftTypes$, assignees: assignees$ }).subscribe(
        ({ shiftTypes, assignees }) => {
          const shiftBody = shiftTypes?.body ?? shiftTypes;
          const shiftList = Array.isArray(shiftBody)
            ? shiftBody
            : (shiftBody?.content ?? shiftBody?.data ?? []);
          this.shiftTypeOptions = Array.isArray(shiftList) ? shiftList : [];

          const assigneeList = Array.isArray(assignees)
            ? assignees
            : (assignees?.content ?? assignees?.data ?? []);
          if (this.isGroupsMode) {
            this.groupOptions = Array.isArray(assigneeList) ? assigneeList : [];
          } else {
            this.userOptions = Array.isArray(assigneeList) ? assigneeList : [];
          }

          if (this.isEditMode) {
            this.loadAssignmentDetails();
          } else {
            this.prefillLockedUser();
            this.prefillLockedGroup();
            this.prefillLockedShiftType();
            this.spinner.hide();
          }
        }
      )
    );
  }

  /** Prefill + ensure option exists when Assign opens from user Shifts tab */
  private prefillLockedUser() {
    if (!this.isUsersMode) {
      return;
    }
    const rawId = this.data?.userId ?? this.data?.user?.id ?? null;
    if (rawId == null || rawId === '') {
      return;
    }

    const match = this.userOptions.find((u: any) => String(u?.id) === String(rawId));
    if (match) {
      this.selectedUser = match.id;
      return;
    }

    const fullName =
      this.data?.fullName ||
      this.data?.user?.fullName ||
      [this.data?.user?.firstName, this.data?.user?.lastName].filter(Boolean).join(' ').trim() ||
      'Selected User';
    this.userOptions = [{ id: rawId, fullName }, ...this.userOptions];
    this.selectedUser = rawId;
  }

  /** Prefill + ensure option exists when Assign opens from group Shifts tab */
  private prefillLockedGroup() {
    if (!this.isGroupsMode) {
      return;
    }
    const rawId =
      this.data?.userGroupId ?? this.data?.groupId ?? this.data?.group?.id ?? null;
    if (rawId == null || rawId === '') {
      return;
    }

    const match = this.groupOptions.find((g: any) => String(g?.id) === String(rawId));
    if (match) {
      this.selectedGroup = match.id;
      return;
    }

    const name =
      this.data?.groupName ||
      this.data?.group?.name ||
      this.data?.userGroupName ||
      'Selected Group';
    this.groupOptions = [{ id: rawId, name }, ...this.groupOptions];
    this.selectedGroup = rawId;
  }

  /** Prefill + lock shift type when Assign opens from shift type details */
  private prefillLockedShiftType() {
    const rawId = this.data?.shiftTypeId ?? this.data?.shiftType?.id ?? null;
    if (rawId == null || rawId === '') {
      return;
    }

    const match = this.shiftTypeOptions.find((s: any) => String(s?.id) === String(rawId));
    const value = match ? match.id : rawId;
    if (!match) {
      const name =
        this.data?.shiftTypeName ||
        this.data?.shiftType?.name ||
        this.data?.name ||
        'Selected Shift Type';
      this.shiftTypeOptions = [{ id: rawId, name, code: this.data?.code }, ...this.shiftTypeOptions];
    }

    if (this.isUsersMode) {
      this.selectedUserShiftType = value;
    } else {
      this.selectedGroupShiftType = value;
    }
  }

  /********************************** GET /shift-assignments/{{id}} **************************/
  private loadAssignmentDetails() {
    if (!this.assignmentId) {
      this.spinner.hide();
      return;
    }

    this.push(
      this.shiftsService.getShiftAssignmentById(this.assignmentId).subscribe(
        (res: any) => {
          this.spinner.hide();
          const body = res?.body ?? res ?? {};
          this.patchAssignmentForm(body);
        },
        (err: any) => {
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            err.status,
            err.error ? err.error.title : null,
            err.error ? err.error.title : null,
            'error',
            2000
          );
        }
      )
    );
  }

  private patchAssignmentForm(body: any) {
    const assigneeType = String(body.assigneeType || '').toUpperCase();
    if (assigneeType === 'USER_GROUP' || assigneeType === 'GROUP') {
      this.viewMode = 'groups';
    } else if (assigneeType === 'USER') {
      this.viewMode = 'users';
    }

    const shiftTypeId =
      body.shiftTypeId ?? body.shiftType?.id ?? body.userShiftTypeId ?? null;
    const startRaw = body.effectiveStartDate || body.effStart || body.startDate;
    const endRaw = body.effectiveEndDate || body.effEnd || body.endDate;
    const startDate = this.parseApiDate(startRaw);
    const endDate = this.parseApiDate(endRaw);

    if (this.isUsersMode) {
      const userId =
        body.userId ?? body.user?.id ?? body.assigneeId ?? this.data?.userId ?? null;
      const match = this.userOptions.find((u: any) => String(u?.id) === String(userId));
      this.selectedUser = match ? match.id : userId;
      if (this.isUserLocked && userId != null && !match) {
        this.prefillLockedUser();
      }
      this.selectedUserShiftType = shiftTypeId;
      this.userStartDate = startDate;
      this.userEndDate = endDate;
    } else {
      const groupId =
        body.userGroupId ??
        body.userGroup?.id ??
        body.groupId ??
        body.assigneeId ??
        this.data?.userGroupId ??
        null;
      const match = this.groupOptions.find((g: any) => String(g?.id) === String(groupId));
      this.selectedGroup = match ? match.id : groupId;
      if (this.isGroupLocked && groupId != null && !match) {
        this.prefillLockedGroup();
      }
      this.selectedGroupShiftType = shiftTypeId;
      this.groupStartDate = startDate;
      this.groupEndDate = endDate;
    }
  }

  private parseApiDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    const m = moment(value);
    return m.isValid() ? m.toDate() : null;
  }

  onTabChange(event: any) {
    this.activeTabIndex = event?.index ?? 0;
    this.submitTouched = false;
    if (!this.isEditMode) {
      this.resetTabFields();
    }
  }

  /** Clear both tabs' form values when switching tabs */
  private resetTabFields() {
    this.selectedUser = null;
    this.selectedUserShiftType = null;
    this.userStartDate = null;
    this.userEndDate = null;

    this.selectedGroup = null;
    this.selectedGroupShiftType = null;
    this.groupStartDate = null;
    this.groupEndDate = null;
  }

  get isUserDateRangeInvalid(): boolean {
    return this.isDateRangeInvalid(this.userStartDate, this.userEndDate);
  }

  get isGroupDateRangeInvalid(): boolean {
    return this.isDateRangeInvalid(this.groupStartDate, this.groupEndDate);
  }

  private isDateRangeInvalid(startDate: Date | null, endDate: Date | null): boolean {
    if (!startDate || !endDate) {
      return false;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end.getTime() < start.getTime();
  }

  isUserTabValid(): boolean {
    return !!(
      this.selectedUser &&
      this.selectedUserShiftType &&
      this.userStartDate &&
      !this.isUserDateRangeInvalid
    );
  }

  isGroupTabValid(): boolean {
    return !!(
      this.selectedGroup &&
      this.selectedGroupShiftType &&
      this.groupStartDate &&
      !this.isGroupDateRangeInvalid
    );
  }

  private formatStartDate(date: Date): string {
    return this.util
      .timeFormatter(
        moment('00:00:00', 'h:mm A').format('h:mm A'),
        moment(date).format('YYYY-MM-DD')
      )
      .toISOString();
  }

  private formatEndDate(date: Date | null): string | null {
    if (!date) {
      return null;
    }
    return this.util
      .timeFormatter(
        moment('23:59:00', 'h:mm A').format('h:mm A'),
        moment(date).format('YYYY-MM-DD')
      )
      .toISOString();
  }

  /** Build payload for create/update (POST/PUT /shift-assignments) */
  private buildPayload(): any | null {
    let body: any = null;

    if (this.isUsersMode) {
      body = {
        assigneeType: 'USER',
        shiftTypeId: this.selectedUserShiftType,
        userId: this.selectedUser,
        effectiveStartDate: this.formatStartDate(this.userStartDate as Date),
        effectiveEndDate: this.formatEndDate(this.userEndDate),
      };
    } else if (this.isGroupsMode) {
      body = {
        assigneeType: 'USER_GROUP',
        shiftTypeId: this.selectedGroupShiftType,
        userGroupId: this.selectedGroup,
        effectiveStartDate: this.formatStartDate(this.groupStartDate as Date),
        effectiveEndDate: this.formatEndDate(this.groupEndDate),
      };
    }

    if (body && this.mode === 'edit' && this.assignmentId != null) {
      body.id = this.assignmentId;
    }

    return body;
  }

  onSave() {
    this.submitTouched = true;
    if (this.saving) {
      return;
    }

    if (this.isUsersMode) {
      if (!this.isUserTabValid()) {
        return;
      }
    } else if (this.isGroupsMode) {
      if (!this.isGroupTabValid()) {
        return;
      }
    } else {
      return;
    }

    const body = this.buildPayload();
    if (!body) {
      return;
    }

    if (this.mode === 'edit' && body.id == null) {
      return;
    }

    this.saving = true;
    this.spinner.show();

    const request$ =
      this.mode === 'edit'
        ? this.shiftsService.updateShiftAssignment(body)
        : this.shiftsService.createShiftAssignment(body);

    this.push(
      request$.subscribe(
        () => {
          this.saving = false;
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            200,
            null,
            this.translate?.general?.messages?.savedSuccessfully || 'Saved successfully',
            'success',
            2000
          );
          this.modalRef.hide();
        },
        (err: any) => {
          this.saving = false;
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            err.status,
            err.error ? err.error.title : null,
            err.error ? err.error.title : null,
            'error',
            2000
          );
        }
      )
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  /******************************************** Go Back ******************************************/
  public goBack() {
    this.location.back();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
